import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { CommonIcons } from 'common-src/assets/Icons';
import { getIcd10Codes } from 'common-src/models/Icd10Code';
import {
  getIcd10CodeDescription,
  getPatientMedicalProblems,
} from 'common-src/models/PatientMedicalProblem';

import { InfoItem, SeparatorLine } from 'src/components/elements';
import useCustomSelector from 'src/hooks/useCustomSelector';

import styles from './Conditions.module.scss';

const ConditionsModule = ({ patientId, conditions, setConditions }) => {
  const allConditions = useCustomSelector((state) => getPatientMedicalProblems(state, patientId));
  const icd10Codes = useCustomSelector((state) => getIcd10Codes(state));

  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (_.isEmpty(allConditions)) return;

    const transformedConditions = allConditions.map((condition) => ({
      icd10Code: condition.icd10Code || '-',
      description: getIcd10CodeDescription(icd10Codes, condition.icd10Code),
    }));
    setConditions(transformedConditions);
  }, [allConditions, icd10Codes, setConditions]);

  const renderConditions = () => {
    if (_.isEmpty(conditions)) {
      return <span className={styles.placeholder}>No conditions available</span>;
    }

    return conditions.map((condition, index) => (
      <div key={index}>
        <div className={styles.grid}>
          <InfoItem textId="icd10-code" title="Code" content={condition.icd10Code} isSmall />
          <InfoItem
            textId="description"
            title="Description"
            content={condition.description}
            isSmall
          />
        </div>
        <SeparatorLine classNames={['m-b-20', 'm-t-20']} />
      </div>
    ));
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.header}
        role="button"
        tabIndex={0}
        onClick={() => setIsExpanded((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setIsExpanded((prev) => !prev);
        }}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span className={[styles.title, 'font-w-700', 'font-s-18'].join(' ')}>Conditions</span>
        <img
          className={[styles.toggleButton, isExpanded ? styles.expanded : styles.collapsed].join(
            ' ',
          )}
          src={CommonIcons.downIcon}
          alt="expand-collapse-btn"
          role="presentation"
          aria-hidden="true"
        />
      </div>

      {isExpanded && <div className={styles.wrapper}>{renderConditions()}</div>}
    </div>
  );
};

ConditionsModule.propTypes = {
  patientId: PropTypes.number,
  conditions: PropTypes.array,
  setConditions: PropTypes.func,
};

export default ConditionsModule;
